/**
 * Date utility functions for consistent date handling
 */

import {
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  formatDistance,
  formatDistanceToNow,
  subMonths,
} from "date-fns";
import { de, enUS, nl } from "date-fns/locale";

/**
 * Returns the current year and month as a tuple
 * @returns [year, month] - year as string (YYYY), month as string (MM)
 */
export const getCurrentYearMonth = (): [string, string] => {
  const today = new Date();
  const year = today.getFullYear().toString();
  const month = (today.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-based, pad with 0
  return [year, month];
};

/**
 * Returns the year and month from a Date object as a tuple
 * @param date - The date object to extract year and month from
 * @returns [year, month] - year as string (YYYY), month as string (MM)
 */
export const getYearMonthFromDate = (date: Date): [string, string] => {
  const year = date.getFullYear().toString();
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-based, pad with 0
  return [year, month];
};

/**
 * Creates a Date object from year and month strings
 * @param year - Year in YYYY format
 * @param month - Month in MM format (01-12)
 * @returns Date object set to the first day of the specified year and month
 */
export const createDateFromYearMonth = (year: string, month: string): Date => {
  const yearNum = Number.parseInt(year, 10);
  const monthNum = Number.parseInt(month, 10) - 1; // Month is 0-based in Date constructor
  return new Date(yearNum, monthNum, 1);
};

/**
 * Creates a Date object from a year-month tuple
 * @param yearMonth - Tuple of [year, month] where year is YYYY format and month is MM format (01-12)
 * @returns Date object set to the first day of the specified year and month
 */
export const createDateFromTuple = (yearMonth: [string, string]): Date => {
  const [year, month] = yearMonth;
  return createDateFromYearMonth(year, month);
};

/**
 * Returns the formatted date string in YYYY-MM format
 * @param date - The date object to format
 * @returns The date formatted as YYYY-MM
 */
export const formatYearMonth = (date: Date): string => {
  const [year, month] = getYearMonthFromDate(date);
  return `${year}-${month}`;
};

export const isYearMonthInFuture = (yearMonth: [string, string]): boolean => {
  const selectedDate = createDateFromTuple(yearMonth);
  const today = createDateFromTuple(getCurrentYearMonth());

  return selectedDate > today;
};

/**
 * Returns the previous year and month as a tuple
 * Handles year rollover (e.g., January -> December of previous year)
 * @returns [year, month] - year as string (YYYY), month as string (MM)
 */
export function getPreviousYearMonth(): [string, string] {
  return getYearMonthFromDate(subMonths(new Date(), 1));
}

/**
 * Gets the date-fns locale object based on language code
 * @param locale - Language code ('en', 'de', 'nl')
 * @returns date-fns locale object
 */
function getDateFnsLocale(locale?: string) {
  switch (locale?.toLowerCase()) {
  case "de":
    return de;
  case "nl":
    return nl;
  case "en":
  default:
    return enUS;
  }
}

/**
 * Calculates the duration between two dates and returns the value with the most appropriate unit
 * Uses years if >= 1 year, months if >= 1 month, otherwise days
 * If end date is null, calculates from start to today
 * @param start - Start date (string or null)
 * @param end - End date (string or null)
 * @returns Object with value and unit ('days' | 'months' | 'years' | 'ongoing')
 */
export function calculateDuration(
  start?: string | null,
  end?: string | null
): { value: number; unit: "days" | "months" | "years" | "ongoing" } {
  if (!start) {
    return { value: 0, unit: "days" };
  }

  // If end date is null, undefined, or empty string, return ongoing
  if (!end || end.trim() === "") {
    return { value: 0, unit: "ongoing" };
  }

  const startDate = new Date(start);
  const endDate = new Date(end);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return { value: 0, unit: "days" };
  }

  const days = differenceInDays(endDate, startDate);
  const months = differenceInMonths(endDate, startDate);
  const years = differenceInYears(endDate, startDate);

  // Use years if >= 1 year
  if (years >= 1) {
    return { value: years, unit: "years" };
  }

  // Use months if >= 1 month
  if (months >= 1) {
    return { value: months, unit: "months" };
  }

  // Otherwise use days
  return { value: days, unit: "days" };
}

/**
 * Formats the duration between two dates using date-fns with internationalization
 * If end date is null/empty, formats distance from start to now (ongoing)
 * @param start - Start date (string or null)
 * @param end - End date (string or null)
 * @param locale - Language code for locale ('en', 'de', 'nl')
 * @returns Translated duration string (e.g., "5 days", "2 months", "about 1 year", "3 days ago" for ongoing)
 */
export function formatDuration(
  start?: string | null,
  end?: string | null,
  locale?: string
): string {
  if (!start) {
    return "";
  }

  const startDate = new Date(start);
  if (Number.isNaN(startDate.getTime())) {
    return "";
  }

  const dateFnsLocale = getDateFnsLocale(locale);

  // If end date is null, undefined, or empty string, format as distance to now (ongoing)
  if (!end || end.trim() === "") {
    return formatDistanceToNow(startDate, {
      addSuffix: false,
      locale: dateFnsLocale,
    });
  }

  const endDate = new Date(end);
  if (Number.isNaN(endDate.getTime())) {
    return "";
  }

  // Format distance between start and end dates
  return formatDistance(startDate, endDate, {
    locale: dateFnsLocale,
  });
}
